import { useState } from 'react';
import clsx from 'clsx';
import { PhoneMissed, PhoneForwarded, ThumbsDown } from 'lucide-react';
import { recordSweeperOutcome } from '@/domain/sweeperFlow';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { gapKeyFor, outcomeLabel, sweeperOutcomeId } from '@/domain/sweeperOutcomes';
import type { SweeperOutcomeKind } from '@/domain/types';

const OUTCOMES: Array<{ key: SweeperOutcomeKind; label: string; hint: string; Icon: typeof ThumbsDown }> = [
  { key: 'turned-down', label: 'Turned down', hint: 'Off this gap, on to the next', Icon: ThumbsDown },
  { key: 'no-answer', label: 'No answer', hint: 'Their callback comes to you', Icon: PhoneMissed },
  { key: 'call-back', label: 'Call me back', hint: 'Their callback comes to you', Icon: PhoneForwarded },
];

/**
 * What the Sweeper's call to this gap candidate came to (B7, 8xat D08-16 / 75nh
 * D08-26). Its own component, inside the pinned gap-fill panel, so the Agent's
 * "Declined" outcome in QueueWorkModal stays untouched (B8 works there).
 *   Turned down  — "not this slot": the client leaves this gap's list and the
 *                  ladder moves on; other gaps still offer them. Not the Agent's
 *                  "said no", which turns the client red in the sequence.
 *   No answer / Call me back — still listed, marked, and their callback rings
 *                  this Sweeper for 7 days (the Callbacks band).
 */
export default function GapOutcomePanel({
  customerId,
  artistId,
  dayIso,
  onRecorded,
}: {
  customerId: string;
  artistId: string;
  dayIso: string;
  onRecorded: () => void;
}) {
  const [note, setNote] = useState('');
  const current = useBrainStore((s) => s.sweeperOutcomes[sweeperOutcomeId(gapKeyFor(artistId, dayIso), customerId)]);
  const pushNotification = useUIStore((s) => s.pushNotification);

  const record = (outcome: SweeperOutcomeKind) => {
    const saved = recordSweeperOutcome({ customerId, artistId, dayIso, outcome, note });
    if (!saved) return;
    pushNotification({
      intensity: 'info',
      title: outcomeLabel(outcome),
      body:
        outcome === 'turned-down'
          ? 'Off this gap. The next candidate is up.'
          : 'Still on the list. If they ring back in the next 7 days, it comes to you.',
      ttlMs: 4000,
      customerId,
    });
    onRecorded();
  };

  return (
    <section data-testid="gap-outcome-panel" className="mt-3">
      <p className="label-small-caps text-gold-bright mb-1.5">How did the call go?</p>
      {current ? (
        <p className="font-serif text-[12px] text-cream-soft mb-1.5" data-testid="gap-outcome-current">
          Last time: {outcomeLabel(current.outcome)}
        </p>
      ) : null}
      <div className="grid grid-cols-3 gap-1.5">
        {OUTCOMES.map(({ key, label, hint, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => record(key)}
            data-testid={`gap-outcome-${key}`}
            title={hint}
            className={clsx(
              'flex flex-col items-start gap-0.5 rounded-md border px-2.5 py-2 text-left transition',
              current?.outcome === key ? 'border-gold-bright bg-gold/10' : 'border-gold-dim/30 hover:border-gold-bright/50',
            )}
          >
            <span className="flex items-center gap-1.5 font-inter text-[12px] text-cream">
              <Icon size={12} className="text-gold-bright" /> {label}
            </span>
            <span className="font-serif text-[10.5px] text-cream-faint leading-snug">{hint}</span>
          </button>
        ))}
      </div>
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note (optional)"
        data-testid="gap-outcome-note"
        className="mt-1.5 w-full rounded-md border border-gold-dim/30 bg-ink-black px-2.5 py-1.5 text-[12px] text-cream focus:outline-none focus:border-gold-bright"
      />
    </section>
  );
}
